import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "../UserContext";
import { ThemeContext } from "../ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Sparkles, Loader2, AlertCircle, Dice6 } from "lucide-react";
import api from "../api";

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  // Hover colors: Light mode: #06bae4, Dark mode: #ff9e42
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 2px ${hexToRgba(hoverColor, 0.3)}, 0 0 20px ${hexToRgba(hoverColor, 0.5)}, 0 0 40px ${hexToRgba(hoverColor, 0.3)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      {children}
    </motion.div>
  );
};

const ChooseTopicsPage = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const [topics, setTopics] = useState([]);
  const [rolling, setRolling] = useState(false);
  const [stopped, setStopped] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showDifficultyPopup, setShowDifficultyPopup] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const playButtonRef = useRef(null);
  useEffect(() => {
    fetchTopics();
  }, [user]);
  const fetchTopics = async () => {
    setLoading(true);
    try {
      const response = await api.get("api/contenttype/");
      const sortedTopics = sortTopics(response.data);
      setTopics(sortedTopics);
    } catch (error) {
      console.error("There was an error fetching the topics!", error);
    } finally {
      setLoading(false);
    }
  };

  const sortTopics = (topicsToSort) => {
    const order = [
      "All Content",
      "All Math",
      "Advanced math",
      "Problem Solving and Data Analysis",
      "Geometry and Trigonometry",
      "Algebra",
      "All English",
      "Writing",
      "Reading",
      "Sentence Completion",
    ];
    return topicsToSort.sort((a, b) => {
      const indexA = order.indexOf(a.content_name);
      const indexB = order.indexOf(b.content_name);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  const handleTopicSelection = (index, topic) => {
    if (!rolling) {
      setSelectedIndex(index);
      setSelectedTopic(topic);
      setStopped(false);
      
      // Scroll to play button after a short delay to allow state update
      setTimeout(() => {
        if (playButtonRef.current) {
          playButtonRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 100);
    }
  };

  const handleStartGameClick = (categoryName) => {
    if (!categoryName) return;
    setShowDifficultyPopup(true);
  };

  const handleDifficultySelection = (difficulty) => {
    setSelectedDifficulty(difficulty);
    setShowDifficultyPopup(false);
    // Navigate with state instead of URL params
    navigate('/play-game', { 
      state: { 
        content_name: selectedTopic.content_name,
        difficulty_level: difficulty,
        allowed: true, 
        fromChooseTopics: true 
      } 
    });
  };

  const handleRandomTopicClick = () => {
    if (topics.length === 0) return;
    setRolling(true);
    setStopped(false);
    setSelectedTopic(null);
    setSelectedIndex(null);

    let currentRollIndex = 0;
    let rollCount = 0;
    const totalRolls = Math.floor(Math.random() * topics.length * 2) + topics.length * 2;
    const rollSpeed = 100;

    const interval = setInterval(() => {
      setSelectedIndex(currentRollIndex);
      currentRollIndex = (currentRollIndex + 1) % topics.length;
      rollCount++;

      if (rollCount >= totalRolls) {
        clearInterval(interval);
        const finalSelectedIndex = (currentRollIndex -1 + topics.length) % topics.length;
        setSelectedIndex(finalSelectedIndex);
        setSelectedTopic(topics[finalSelectedIndex]);
        setRolling(false);
        setStopped(true);
        
        // Scroll to play button after random selection
        setTimeout(() => {
          if (playButtonRef.current) {
            playButtonRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 200);
      }
    }, rollSpeed);
  };
  
  const playButtonText = selectedTopic && !rolling ? "Play Selected Subject" : "Choose a Subject or Go Random";

  return (
    <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Main Content */}
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6">
        {/* Animated Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8 relative"
        >
          <h1
            key={`title-${isDarkMode}`}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Choose Your Subject
          </h1>
        </motion.div>

        <div className="w-full max-w-4xl mx-auto">

          {loading ? (
            <Card delay={0.2} className="w-full">
              <div className="w-full flex flex-col justify-center items-center min-h-[250px]">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="mb-4"
                >
                  <Loader2 className="w-12 h-12 text-cyan-500" />
                </motion.div>
                <p className="dark:text-lime-300 text-xl font-zonaProRegular" style={{ color: '#34c759' }}>Loading Subjects...</p>
              </div>
            </Card>
          ) : topics.length === 0 ? (
            <Card delay={0.2} className="w-full">
              <div className="w-full flex flex-col justify-center items-center min-h-[250px] text-center">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="mb-4"
                >
                  <AlertCircle className="w-16 h-16 text-red-400" />
                </motion.div>
                <h2 className="text-2xl font-semibold text-red-500 dark:text-red-400 mb-2 font-zonaProUltra">No Subjects Available</h2>
                <p className="text-gray-600 dark:text-gray-200 font-zonaProRegular">We couldn't load any subjects at this time. Please try again later.</p>
              </div>
            </Card>
          ) : (
            <Card delay={0.2} className="w-full p-6 md:p-8 lg:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {topics.map((topic, i) => {
                  const isSelected = !rolling && selectedTopic && selectedTopic.content_type_id === topic.content_type_id;
                  const isRolling = rolling && selectedIndex === i;
                  const displayName = topic.content_name === 'Sentence Completion' 
                    ? 'Vocablary In Context' 
                    : topic.content_name === 'Writing' 
                    ? 'Writing (Standard English Conventions and Expression of Ideas)' 
                    : topic.content_name === 'Reading' 
                    ? 'Reading (Craft & Structure and Information & Ideas)' 
                    : topic.content_name;

                  return (
                    <motion.div
                      key={topic.content_type_id || i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + i * 0.05 }}
                      whileHover={!rolling ? { scale: 1.03, x: 5, y: -2 } : {}}
                      whileTap={!rolling ? { scale: 0.98 } : {}}
                      className={`flex items-center space-x-4 p-5 md:p-6 rounded-xl transition-all duration-200 cursor-pointer min-h-[65px] md:min-h-[75px]
                        ${isSelected 
                          ? `text-white shadow-xl ring-3 ${isDarkMode ? 'bg-[#ff7a00] ring-[#ff7a00]/50' : 'bg-[#212D82] ring-[#212D82]/50'}` 
                          : isRolling
                          ? `scale-105 shadow-lg ring-2 ${isDarkMode ? 'bg-[#ff7a00]/30 ring-[#ff7a00]/50' : 'bg-[#212D82]/30 ring-[#212D82]/50'}`
                          : 'bg-white/60 dark:bg-gray-700/60 hover:bg-white/80 dark:hover:bg-gray-700/80 border-2 border-gray-200 dark:border-gray-600 hover:border-cyan-300 dark:hover:border-cyan-600 hover:shadow-lg'
                        }`}
                      onClick={() => handleTopicSelection(i, topic)}
                    >
                      <input
                        type="radio"
                        name="topic"
                        id={`topic-${topic.content_type_id || i}`}
                        className={`h-6 w-6 md:h-7 md:w-7 transition-colors duration-150 flex-shrink-0
                          ${isSelected 
                            ? 'accent-white border-white' 
                            : 'border-gray-300 dark:border-gray-500'
                          } 
                          ${rolling ? 'opacity-50' : ''}`}
                        style={!isSelected ? { accentColor: '#34c759' } : {}}
                        checked={isSelected || isRolling}
                        onChange={() => handleTopicSelection(i, topic)}
                        disabled={rolling}
                      />
                      <label
                        htmlFor={`topic-${topic.content_type_id || i}`}
                        className={`text-base md:text-lg lg:text-xl flex-1 text-left font-zonaProRegular leading-relaxed
                          ${isSelected 
                            ? 'text-white font-semibold' 
                            : 'text-gray-700 dark:text-gray-100'
                          } 
                          ${rolling && !isRolling ? 'opacity-50' : 'cursor-pointer'}`}
                      >
                        {displayName}
                      </label>
                    </motion.div>
                  );
                })}
              </div>
            </Card>
          )}

          <div className="mt-8 text-center space-y-5">
            {(selectedTopic && (stopped || (!rolling && selectedTopic))) && (
              <motion.button
                ref={playButtonRef}
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  scale: [1, 1.05, 1],
                  boxShadow: [
                    '0 4px 6px rgba(0, 0, 0, 0.1)',
                    `0 0 30px ${isDarkMode ? 'rgba(255, 122, 0, 0.8)' : 'rgba(33, 45, 130, 0.8)'}`,
                    '0 4px 6px rgba(0, 0, 0, 0.1)'
                  ]
                }}
                transition={{ 
                  delay: 0.4,
                  scale: {
                    duration: 2,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut'
                  },
                  boxShadow: {
                    duration: 2,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut'
                  }
                }}
                onClick={() => handleStartGameClick(selectedTopic.content_name)}
                className="block max-w-md mx-auto py-4 px-12 text-white  text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full hover:shadow-lg transition-all relative"
                style={{ 
                  backgroundColor: isDarkMode ? '#ff7a00' : '#212D82',
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {`Play ${selectedTopic.content_name === 'Sentence Completion' ? 'Vocablary In Context' : selectedTopic.content_name}`}
              </motion.button>
            )}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              onClick={handleRandomTopicClick}
              disabled={rolling || loading || topics.length === 0}
              className="py-4 px-12 bg-[#34c759] text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
              whileHover={!rolling && !loading && topics.length > 0 ? { scale: 1.05 } : {}}
              whileTap={!rolling && !loading && topics.length > 0 ? { scale: 0.95 } : {}}
            >
              {rolling ? (
                <span className="flex items-center justify-center gap-2">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Dice6 className="w-5 h-5" />
                  </motion.div>
                  Rolling...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Dice6 className="w-5 h-5" />
                  Try Random Subject
                </span>
              )}
            </motion.button>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-6"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/solo"
                  className="inline-block font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-100 font-zonaProRegular text-base sm:text-lg md:text-xl transition-colors"
                  >
                  ← Back to Rules
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Difficulty Selection Popup */}
      <AnimatePresence>
        {showDifficultyPopup && selectedTopic && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowDifficultyPopup(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl shadow-2xl p-8 w-full max-w-md border border-white/20 dark:border-gray-700/50"
            >
              <div className="text-center mb-6">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="inline-block mb-4"
                >
                  <Sparkles className="w-12 h-12 text-cyan-500" />
                </motion.div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-zonaProUltra text-gray-800 dark:text-white mb-2">Select Difficulty</h2>
                <p className="text-gray-600 dark:text-gray-200 font-zonaProRegular">
                  Choose the difficulty level for <strong className="text-gray-800 dark:text-white">{selectedTopic.content_name === 'Sentence Completion' ? 'Vocabulary In Context' : selectedTopic.content_name}</strong>
                </p>
              </div>
              
              <div className="space-y-3 mb-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDifficultySelection('random')}
                  className="w-full py-3 px-6 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 text-purple-800 dark:text-purple-200 text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150 border-2 border-purple-300 dark:border-purple-700"
                >
                  🎲 Random (Mixed Difficulty)
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDifficultySelection('easy')}
                  className="w-full py-3 px-6 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-800 dark:text-green-200 text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150 border-2 border-green-300 dark:border-green-700"
                >
                  🟢 Easy (+100/-100 points)
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDifficultySelection('medium')}
                  className="w-full py-3 px-6 bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200 text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150 border-2 border-yellow-300 dark:border-yellow-700"
                >
                  🟡 Medium (+200/-200 points)
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDifficultySelection('hard')}
                  className="w-full py-3 px-6 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-800 dark:text-red-200 text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150 border-2 border-red-300 dark:border-red-700"
                >
                  🔴 Hard (+300/-300 points)
                </motion.button>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowDifficultyPopup(false)}
                className="w-full py-2 px-6 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-100 text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150"
              >
                Cancel
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChooseTopicsPage;